import { getPrayerAccessIdentity, getTier } from './entitlements';

/**
 * Speech service for Beloved — read-aloud and voice input.
 *
 * This is the single biggest accessibility win for our 50+ audience, so the
 * experience must feel calm and human, never robotic: a gentle reading rate and
 * an en-GB voice where one is available.
 *
 * SAFE TO IMPORT EVERYWHERE. Every function feature-detects the underlying Web
 * Speech APIs (`speechSynthesis` for reading, `SpeechRecognition` /
 * `webkitSpeechRecognition` for listening) and does nothing — throwing never —
 * when they are absent. jsdom (the test environment) has neither, which is
 * exactly why the guards are exercised there. The UI hides each button entirely
 * when its capability is missing, so these no-ops are only ever a safety net.
 */

/** A calm, unhurried reading rate (below the 1.0 default so it never rushes). */
export const SPEAK_RATE = 0.86;

/** A slightly lower pitch gives prayer readings a warmer, grounded tone. */
export const SPEAK_PITCH = 0.92;

/** Preferred spoken language: British English. */
const EN_GB = 'en-GB';

/**
 * Cloud neural speech is opt-in at build time and also guarded on the server.
 * Device speech remains the permanent offline/privacy fallback.
 */
const ENHANCED_SPEECH_ENABLED =
  String(process.env.REACT_APP_ENHANCED_SPEECH_ENABLED || '').trim().toLowerCase() === 'true';

export function usesEnhancedVoice() {
  return ENHANCED_SPEECH_ENABLED && getTier() === 'plus';
}

/**
 * Call a callback if — and only if — it is a function.
 * @param {unknown} callback
 * @returns {void}
 */
function finish(callback) {
  if (typeof callback === 'function') callback();
}

/**
 * Whether a value is a non-empty string.
 * @param {unknown} value
 * @returns {boolean}
 */
function isNonEmptyString(value) {
  return typeof value === 'string' && value.trim().length > 0;
}

// ============================================================
// Read-aloud (speech synthesis)
// ============================================================

/**
 * Whether this device can read text aloud.
 * @returns {boolean}
 */
function canSpeakNatively() {
  return (
    typeof window !== 'undefined' &&
    typeof window.speechSynthesis !== 'undefined' &&
    typeof window.SpeechSynthesisUtterance !== 'undefined'
  );
}

function canSpeakWithEnhancedVoice() {
  return (
    usesEnhancedVoice() &&
    typeof window !== 'undefined' &&
    typeof window.fetch === 'function' &&
    typeof window.Audio === 'function' &&
    typeof window.AbortController === 'function' &&
    window.URL &&
    typeof window.URL.createObjectURL === 'function'
  );
}

export function canSpeak() {
  return canSpeakNatively() || canSpeakWithEnhancedVoice();
}

const NATURAL_VOICE_HINTS = ['natural', 'neural', 'premium', 'enhanced', 'high quality'];
const REVERENT_VOICE_HINTS = [
  'arthur',
  'daniel',
  'george',
  'james',
  'oliver',
  'reed',
  'rishi',
  'ryan',
  'serena',
  'thomas',
];
const NOVELTY_VOICE_HINTS = [
  'bad news',
  'bahh',
  'bells',
  'boing',
  'bubbles',
  'cellos',
  'organ',
  'superstar',
  'trinoids',
  'whisper',
  'wobble',
  'zarvox',
];

/**
 * Score a device voice for a warm, reverent English reading.
 * @param {SpeechSynthesisVoice | null} voice
 * @returns {number}
 */
function voiceScore(voice) {
  if (!voice) return Number.NEGATIVE_INFINITY;

  const name = String(voice.name || '').toLowerCase();
  const uri = String(voice.voiceURI || '').toLowerCase();
  const description = `${name} ${uri}`;
  const lang = String(voice.lang || '').toLowerCase();

  if (!lang.startsWith('en')) return Number.NEGATIVE_INFINITY;

  let score = 40;
  if (lang === 'en-gb') score += 75;
  else if (lang.startsWith('en-gb')) score += 70;
  else if (lang.startsWith('en-ie')) score += 52;
  else if (lang.startsWith('en-au') || lang.startsWith('en-nz')) score += 38;

  if (NATURAL_VOICE_HINTS.some((hint) => description.includes(hint))) score += 90;
  if (REVERENT_VOICE_HINTS.some((hint) => description.includes(hint))) score += 34;
  if (NOVELTY_VOICE_HINTS.some((hint) => description.includes(hint))) score -= 250;
  if (voice.default) score += 3;

  return score;
}

/**
 * Choose the best available English voice for prayer.
 * Exported as a pure helper so the ranking stays directly testable.
 * @param {SpeechSynthesisVoice[]} voices
 * @returns {SpeechSynthesisVoice | null}
 */
export function pickReverentVoice(voices) {
  if (!Array.isArray(voices) || voices.length === 0) return null;

  return voices.reduce(
    (best, voice) => (voiceScore(voice) > voiceScore(best) ? voice : best),
    null
  );
}

/**
 * Read the device's current voice list and choose its best prayer voice.
 * @returns {SpeechSynthesisVoice | null}
 */
function pickDeviceVoice() {
  try {
    const voices = window.speechSynthesis.getVoices() || [];
    return pickReverentVoice(voices);
  } catch (e) {
    return null;
  }
}

/**
 * Break text into sentence-sized chunks. iOS Safari silently drops long
 * utterances, so we queue one short sentence at a time for reliability.
 * @param {string} text
 * @returns {string[]}
 */
function chunkIntoSentences(text) {
  const trimmed = text.trim();
  if (!trimmed) return [];
  const matches = trimmed.match(/[^.!?]+[.!?]*\s*/g);
  if (!matches) return [trimmed];
  return matches.map((s) => s.trim()).filter(Boolean);
}

/** The in-flight reading session, so {@link stopSpeaking} can cancel cleanly. */
let activeSpeech = null;

/**
 * Read text aloud with a calm rate and en-GB voice preference. Long text is
 * spoken sentence by sentence for iOS reliability. Safe to call when reading is
 * unavailable — it simply invokes `onEnd` and returns.
 * @param {string} text - the words to read (e.g. a prayer's title and body).
 * @param {() => void} [onEnd] - called once when reading finishes or fails.
 * @param {() => void} [onStart] - called when audible playback begins.
 * @returns {void}
 */
function speakNatively(text, onEnd, onStart) {
  if (!canSpeakNatively() || !isNonEmptyString(text)) {
    finish(onEnd);
    return;
  }

  const synth = window.speechSynthesis;
  // Clear anything already queued so a new reading always starts fresh.
  try {
    synth.cancel();
  } catch (e) {
    finish(onEnd);
    return;
  }

  const session = { cancelled: false };
  activeSpeech = session;

  const chunks = chunkIntoSentences(text);
  const voice = pickDeviceVoice();
  let index = 0;
  let started = false;

  const speakNext = () => {
    if (session.cancelled) return;
    if (index >= chunks.length) {
      activeSpeech = null;
      finish(onEnd);
      return;
    }

    let utterance;
    try {
      utterance = new window.SpeechSynthesisUtterance(chunks[index]);
    } catch (e) {
      session.cancelled = true;
      activeSpeech = null;
      finish(onEnd);
      return;
    }

    utterance.rate = SPEAK_RATE;
    utterance.pitch = SPEAK_PITCH;
    utterance.lang = voice && voice.lang ? voice.lang : EN_GB;
    if (voice) utterance.voice = voice;

    utterance.onend = () => {
      if (session.cancelled) return;
      index += 1;
      speakNext();
    };
    utterance.onerror = () => {
      if (session.cancelled) return;
      session.cancelled = true;
      activeSpeech = null;
      finish(onEnd);
    };

    try {
      if (!started) {
        started = true;
        finish(onStart);
      }
      synth.speak(utterance);
    } catch (e) {
      session.cancelled = true;
      activeSpeech = null;
      finish(onEnd);
    }
  };

  speakNext();
}

let activeEnhancedSpeech = null;

function enhancedSpeechEndpoint() {
  const configured = String(process.env.REACT_APP_SPEECH_ENDPOINT || '').trim();
  if (configured) return configured;
  try {
    if (window.location && window.location.protocol === 'capacitor:') {
      return 'https://belovedapp.net/api/speech';
    }
  } catch (error) {
    // Browser-relative endpoint is the safe default.
  }
  return '/api/speech';
}

function cleanEnhancedSession(session) {
  if (!session) return;
  if (session.audio) {
    session.audio.onended = null;
    session.audio.onerror = null;
    try {
      session.audio.pause();
    } catch (error) {
      // Already stopped.
    }
  }
  if (session.url) {
    try {
      window.URL.revokeObjectURL(session.url);
    } catch (error) {
      // The object URL may already have been released.
    }
  }
  if (activeEnhancedSpeech === session) activeEnhancedSpeech = null;
}

function speakWithEnhancedVoice(text, onEnd, onStart) {
  const controller = new window.AbortController();
  const session = { controller, audio: null, url: '', cancelled: false, completed: false };
  activeEnhancedSpeech = session;

  const complete = () => {
    if (session.completed || session.cancelled) return;
    session.completed = true;
    cleanEnhancedSession(session);
    finish(onEnd);
  };

  const fallBack = () => {
    if (session.cancelled || session.completed) return;
    session.completed = true;
    cleanEnhancedSession(session);
    if (canSpeakNatively()) speakNatively(text, onEnd, onStart);
    else finish(onEnd);
  };

  getPrayerAccessIdentity()
    .then((identity) => {
      if (!identity || !identity.subscriber_id) throw new Error('subscription unavailable');
      return window.fetch(enhancedSpeechEndpoint(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, ...identity }),
        signal: controller.signal,
        cache: 'no-store',
        credentials: 'omit',
      });
    })
    .then((response) => {
      if (!response.ok) throw new Error('speech unavailable');
      return response.blob();
    })
    .then((blob) => {
      if (session.cancelled) return;
      session.url = window.URL.createObjectURL(blob);
      session.audio = new window.Audio(session.url);
      session.audio.preload = 'auto';
      session.audio.onended = complete;
      session.audio.onerror = fallBack;
      const playResult = session.audio.play();
      if (playResult && typeof playResult.then === 'function') {
        playResult.then(() => finish(onStart)).catch(fallBack);
      } else {
        finish(onStart);
      }
    })
    .catch((error) => {
      if (error && error.name === 'AbortError') return;
      fallBack();
    });
}

/**
 * Read text using Beloved's neural voice when explicitly enabled, otherwise
 * use the best available on-device British voice. The server path silently
 * falls back to the device voice during an outage.
 */
export function speak(text, onEnd, onStart) {
  if (!canSpeak() || !isNonEmptyString(text)) {
    finish(onEnd);
    return;
  }
  stopSpeaking();
  if (canSpeakWithEnhancedVoice()) speakWithEnhancedVoice(text, onEnd, onStart);
  else speakNatively(text, onEnd, onStart);
}

/**
 * Stop any reading in progress, immediately and cleanly. Does not call the
 * original `onEnd` (the caller initiated the stop and already knows). Safe to
 * call at any time, including when nothing is being read.
 * @returns {void}
 */
export function stopSpeaking() {
  if (activeEnhancedSpeech) {
    const enhanced = activeEnhancedSpeech;
    enhanced.cancelled = true;
    try {
      enhanced.controller.abort();
    } catch (error) {
      // Already aborted.
    }
    cleanEnhancedSession(enhanced);
  }
  if (activeSpeech) activeSpeech.cancelled = true;
  activeSpeech = null;
  if (!canSpeakNatively()) return;
  try {
    window.speechSynthesis.cancel();
  } catch (e) {
    // Nothing more to do; reading is already stopped or was never possible.
  }
}

// ============================================================
// Voice input (speech recognition)
// ============================================================

/**
 * Resolve the SpeechRecognition constructor (prefixed or unprefixed).
 * @returns {(new () => any) | null}
 */
function getRecognitionCtor() {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

/**
 * Whether this device can listen to the person's voice.
 * @returns {boolean}
 */
export function canListen() {
  return getRecognitionCtor() !== null;
}

/** The in-flight recognition instance, so {@link stopListening} can end it. */
let activeRecognition = null;

/**
 * Listen for a single spoken utterance and hand back the finished words.
 * en-GB, with interim results retained so WebKit cannot discard someone's words
 * when it ends before marking them final. Safe to call when listening is
 * unavailable — it reports the error, invokes `onEnd`, and returns false.
 * @param {(transcript: string) => void} onResult - called with each final phrase.
 * @param {() => void} [onEnd] - called once when listening stops.
 * @param {(code: string) => void} [onError] - called with a browser error code.
 * @param {() => void} [onStart] - called once the microphone session starts.
 * @returns {boolean} whether a recognition session was successfully requested.
 */
export function startListening(onResult, onEnd, onError, onStart) {
  const Recognition = getRecognitionCtor();
  if (!Recognition) {
    if (typeof onError === 'function') onError('unsupported');
    finish(onEnd);
    return false;
  }

  // Never run two sessions at once.
  stopListening();

  let recognition;
  try {
    recognition = new Recognition();
  } catch (e) {
    activeRecognition = null;
    if (typeof onError === 'function') onError('unavailable');
    finish(onEnd);
    return false;
  }

  recognition.lang = EN_GB;
  recognition.interimResults = true;
  recognition.continuous = false;
  recognition.maxAlternatives = 1;

  let latestTranscript = '';
  let deliveredTranscript = '';
  let ended = false;

  const deliver = (text) => {
    const cleaned = typeof text === 'string' ? text.trim() : '';
    if (!cleaned || cleaned === deliveredTranscript || typeof onResult !== 'function') return;
    deliveredTranscript = cleaned;
    onResult(cleaned);
  };

  recognition.onstart = () => {
    if (typeof onStart === 'function') onStart();
  };

  recognition.onresult = (event) => {
    let heardText = '';
    let hasFinalResult = false;
    try {
      for (let i = 0; i < event.results.length; i += 1) {
        const result = event.results[i];
        const alternative = result && result[0];
        if (alternative && typeof alternative.transcript === 'string') {
          heardText += `${alternative.transcript} `;
        }
        // Some WebKit versions omit isFinal; treat that as a complete result.
        if (!result || result.isFinal !== false) hasFinalResult = true;
      }
    } catch (e) {
      return;
    }
    latestTranscript = heardText.trim();
    if (hasFinalResult) deliver(latestTranscript);
  };

  recognition.onerror = (event) => {
    const code = event && typeof event.error === 'string' ? event.error : 'unavailable';
    if (typeof onError === 'function') onError(code);
  };

  recognition.onend = () => {
    if (ended) return;
    ended = true;
    // Safari/WebKit can end after returning only an interim result. Keeping it
    // is more helpful than silently throwing away the person's spoken words.
    deliver(latestTranscript);
    activeRecognition = null;
    finish(onEnd);
  };

  activeRecognition = recognition;

  try {
    recognition.start();
    return true;
  } catch (e) {
    activeRecognition = null;
    if (typeof onError === 'function') onError('unavailable');
    finish(onEnd);
    return false;
  }
}

/**
 * Stop listening if a session is in progress. The session's `onEnd` still fires
 * (via the recognition's own `onend`). Safe to call at any time.
 * @returns {void}
 */
export function stopListening() {
  if (!activeRecognition) return;
  try {
    activeRecognition.stop();
  } catch (e) {
    // Already stopping or never started; nothing to do.
  }
}
